import Alert from "@/components/alert/Alert";
import FormModal from "@/components/modals/FormModal";
import { POST } from "@/services/api";
import { API_ROUTES } from "@/services/environment";
import { Button } from "@thdinc/hpf-shared";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Select, { StylesConfig } from "react-select";
import { BusinessOption, RewardType } from "../interface";

interface AddPointsModalProps {
    isOpen: boolean;
    onClose: () => void;
    businessId: string;
    businesses: BusinessOption[];
    onSuccess: () => void;
}

interface AddPointsFormValues {
    points: number;
    description: string;
}

interface AdjustBulkResult {
    business_id: string;
    success: boolean;
    message?: string;
}

const multiSelectStyles: StylesConfig<BusinessOption, true> = {
    control: (provided, state) => ({
        ...provided,
        backgroundColor: "#f7f8fa",
        borderRadius: "1.5rem",
        minHeight: "49px",
        paddingLeft: "8px",
        border: "none",
        boxShadow: state.menuIsOpen ? "0 0 0 1px #FF5100" : "none",
        "&:hover": { border: "none" },
    }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    multiValue: (provided) => ({
        ...provided,
        backgroundColor: "white",
        borderRadius: "9999px",
        padding: "0 2px",
    }),
};

const AddPointsModal: React.FC<AddPointsModalProps> = ({
    isOpen,
    onClose,
    businessId,
    businesses,
    onSuccess,
}) => {
    const { adjustBulkRewardPointsApi } = API_ROUTES;
    const [rewardType, setRewardType] = useState<RewardType>("plus");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [selectedBusinesses, setSelectedBusinesses] = useState<BusinessOption[]>([]);
    const [businessSelectionError, setBusinessSelectionError] = useState<string>("");
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<AddPointsFormValues>();

    useEffect(() => {
        if (isOpen) {
            const current = businesses.find((b) => b.id === businessId);
            setSelectedBusinesses(current ? [current] : []);
            setSelectAll(false);
            setBusinessSelectionError("");
        }
    }, [isOpen, businessId, businesses]);

    const closeAndReset = () => {
        reset();
        setRewardType("plus");
        onClose();
    };

    const onSubmit = async (data: AddPointsFormValues) => {
        const targetBusinesses = selectAll ? businesses : selectedBusinesses;

        if (targetBusinesses.length === 0) {
            setBusinessSelectionError("Select at least one business");
            return;
        }
        setBusinessSelectionError("");

        try {
            setIsSubmitting(true);
            const response = await POST(adjustBulkRewardPointsApi, {
                business_ids: targetBusinesses.map((b) => b.id),
                points: Number(data.points),
                reward_type: rewardType,
                description: data.description,
            });

            const results: AdjustBulkResult[] = response?.data?.results ?? [];
            const failed = results.filter((r) => !r.success);
            const actionLabel = rewardType === "plus" ? "added" : "deducted";

            if (failed.length === 0) {
                Alert({
                    message: `Points ${actionLabel} successfully for ${targetBusinesses.length} business${targetBusinesses.length > 1 ? "es" : ""
                        }`,
                });
            } else {
                const nameById = new Map(businesses.map((b) => [b.id, b.name]));
                const failedNames = failed.map((f) => nameById.get(f.business_id) ?? f.business_id).join(", ");
                const successCount = targetBusinesses.length - failed.length;
                Alert({
                    type: "error",
                    message:
                        successCount > 0
                            ? `Points ${actionLabel} for ${successCount} business(es). Failed for: ${failedNames}`
                            : `Unable to update points for: ${failedNames}`,
                });
            }

            closeAndReset();
            onSuccess();
        } catch (error: any) {
            Alert({
                type: "error",
                message: error?.response?.data?.message ?? "Unable to update points",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={closeAndReset}
            modalTitle="Add / Deduct Points"
            width="lg:w-2/6 md:w-3/6 w-full"
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex gap-2 mb-4">
                    <button
                        type="button"
                        onClick={() => setRewardType("plus")}
                        className={`flex-1 py-2 rounded-full text-sm font-semibold transition ${rewardType === "plus" ? "bg-primary-red text-white" : "bg-p-gray text-gray-600"
                            }`}
                    >
                        Add Points
                    </button>
                    <button
                        type="button"
                        onClick={() => setRewardType("minus")}
                        className={`flex-1 py-2 rounded-full text-sm font-semibold transition ${rewardType === "minus" ? "bg-primary-red text-white" : "bg-p-gray text-gray-600"
                            }`}
                    >
                        Deduct Points
                    </button>
                </div>

                <div className="mb-4">
                    <p className="font-semibold text-xs mb-2 ml-4">Businesses</p>
                    <label className="flex items-center gap-2 mb-2 ml-4 text-sm text-gray-600 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={(e) => setSelectAll(e.target.checked)}
                            className="accent-primary-red w-4 h-4"
                        />
                        Apply to all businesses
                    </label>
                    {!selectAll && (
                        <Select<BusinessOption, true>
                            isMulti
                            isClearable={false}
                            options={businesses}
                            value={selectedBusinesses}
                            onChange={(selected) => setSelectedBusinesses(selected ? [...selected] : [])}
                            placeholder="Select businesses"
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            styles={multiSelectStyles}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                        />
                    )}
                    {businessSelectionError && (
                        <p className="text-red-500 text-xs mb-2 mt-1 ml-4">{businessSelectionError}</p>
                    )}
                </div>

                <div className="mb-4">
                    <p className="font-semibold text-xs mb-2 ml-4">Points</p>
                    <input
                        type="number"
                        min={1}
                        step={1}
                        className="w-full px-4 py-3 bg-p-gray rounded-full focus:outline-none focus:ring-1 focus:ring-primary-red text-gray-600"
                        placeholder="Enter points"
                        {...register("points", {
                            required: "Points is required",
                            valueAsNumber: true,
                            min: { value: 1, message: "Points must be greater than 0" },
                        })}
                    />
                    <p className="text-red-500 text-xs mb-2 mt-1 ml-4">{errors?.points?.message}</p>
                </div>

                <div className="mb-4">
                    <p className="font-semibold text-xs mb-2 ml-4">Description</p>
                    <textarea
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 bg-p-gray rounded-2xl focus:outline-none focus:ring-1 focus:ring-primary-red text-gray-600 resize-none"
                        placeholder="Reason for this adjustment"
                        {...register("description", { required: "Description is required" })}
                    />
                    <p className="text-red-500 text-xs mb-2 mt-1 ml-4">{errors?.description?.message}</p>
                </div>

                <div className="text-end">
                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="text-center px-6 my-2 rounded-full py-2 bg-primary-red hover:bg-primary-red-button-hover text-white"
                    >
                        {isSubmitting ? "Saving..." : rewardType === "plus" ? "Add Points" : "Deduct Points"}
                    </Button>
                </div>
            </form>
        </FormModal>
    );
};

export default AddPointsModal;
